import { authenticationScreen, roleLabels } from "@groundspark/browser-auth";
import { createBrandGraphic } from "@groundspark/brand";
import type { Account, Organization } from "@groundspark/browser-auth";
import { AdminApi } from "./admin-api";
import { AuthenticationRequiredError, type AuthManager } from "./auth";
import {
  append,
  button,
  buttonIcon,
  dashboardHeader,
  dashboardSelector,
  dashboardTabs,
  element,
  heading,
  table,
  type DashboardTab,
} from "@groundspark/browser-ui";
import {
  type AdminAuditEvent,
  type AdminInvitation,
  type AdminSummary,
  type AdminUser,
  type BranchSummary,
  type ElectoralRegister,
  type GroupSummary,
  invitationStatus,
  type Page,
  type Role,
  roles,
  type UserChanges,
} from "./domain";

type Tab = "users" | "branches" | "groups" | "invitations" | "audit";

type NameChanges =
  | { readonly action: "rename"; readonly name: string }
  | { readonly action: "delete" };

const dateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "medium",
  timeStyle: "short",
});

export class AdminApplication {
  private readonly api: AdminApi;
  private totals: AdminSummary | null = null;
  private branches: readonly BranchSummary[] = [];
  private groups: readonly GroupSummary[] = [];
  private invitationBranchId = "";
  private newBranchName = "";
  private newGroupName = "";
  private organizations: readonly Organization[] | null = null;
  private users: readonly AdminUser[] | null = null;
  private registers: readonly ElectoralRegister[] | null = null;
  private invitations: readonly AdminInvitation[] | null = null;
  private audit: readonly AdminAuditEvent[] | null = null;
  private userCursor: string | null = null;
  private invitationCursor: string | null = null;
  private auditCursor: string | null = null;
  private activeTab: Tab = "users";
  private busy = false;
  private error: string | null = null;
  private invitationEmail = "";
  private invitationNotice: string | null = null;

  public constructor(
    private readonly root: HTMLElement,
    private readonly authentication: AuthManager,
  ) {
    this.api = new AdminApi(authentication);
  }

  public async start(): Promise<void> {
    this.renderLoading("Verifying your session");
    try {
      if (!(await this.authentication.initialize())) {
        this.renderSignIn();
        return;
      }
      if (this.authentication.account?.capabilities.administerOrganization !== true) {
        this.renderAccessDenied();
        return;
      }
      await this.loadAll();
    } catch (error) {
      this.renderSignIn(messageFromError(error));
    }
  }

  private async loadAll(): Promise<void> {
    this.totals = null;
    this.organizations = null;
    this.users = null;
    this.registers = null;
    this.invitations = null;
    this.audit = null;
    this.branches = [];
    this.groups = [];
    this.busy = true;
    this.error = null;
    this.renderDashboard();
    try {
      const [
        totals,
        users,
        registers,
        invitations,
        audit,
        branches,
        groups,
        { organizations, error },
      ] = await Promise.all([
        this.api.getSummary(),
        this.api.listUsers(),
        this.api.listElectoralRegisters(),
        this.api.listInvitations(),
        this.api.listAudit(),
        this.api.listBranches(),
        this.api.listGroups(),
        this.loadOrganizations(),
      ]);
      this.totals = totals;
      this.organizations = organizations;
      this.branches = branches;
      this.groups = groups;
      this.invitationBranchId = branches.some(
        (branch) => branch.id === this.invitationBranchId,
      )
        ? this.invitationBranchId
        : (branches[0]?.id ?? "");
      this.error = error;
      this.users = users.items;
      this.userCursor = users.nextCursor;
      this.registers = registers;
      this.invitations = invitations.items;
      this.invitationCursor = invitations.nextCursor;
      this.audit = audit.items;
      this.auditCursor = audit.nextCursor;
    } catch (error) {
      if (this.handleAuthenticationFailure(error)) return;
      this.error = messageFromError(error);
    } finally {
      this.busy = false;
      this.renderCurrentAuthenticatedState();
    }
  }

  private async loadOrganizations(): Promise<{
    organizations: readonly Organization[] | null;
    error: string | null;
  }> {
    if (this.authentication.account?.role !== "superadmin") {
      return { organizations: null, error: null };
    }
    try {
      return { organizations: await this.authentication.organizations(), error: null };
    } catch (error) {
      if (error instanceof AuthenticationRequiredError) throw error;
      return { organizations: null, error: messageFromError(error) };
    }
  }

  private renderLoading(message: string): void {
    this.replaceRoot(
      this.shell(
        element(
          "main",
          "centred-screen",
          element(
            "section",
            "message-card",
            element("div", "spinner", ""),
            heading(1, message, "message-card__title"),
            element("p", "muted", "Please wait a moment."),
          ),
        ),
      ),
    );
  }

  private renderSignIn(message: string | null = null): void {
    this.replaceRoot(
      this.shell(
        authenticationScreen(this.authentication, () => this.loadAll(), {
          appName: "Admin",
          subtitle: "Manage your organisation",
          error: message,
        }),
      ),
    );
  }

  private renderAccessDenied(): void {
    const account = this.authentication.account;
    this.replaceRoot(
      this.shell(
        element(
          "main",
          "centred-screen",
          element(
            "section",
            "message-card",
            element("p", "eyebrow", "Access denied"),
            heading(1, "Administrator access required", "message-card__title"),
            element(
              "p",
              "muted",
              `This account is not an administrator for ${account?.organization.name ?? "its organisation"}.`,
            ),
            element(
              "p",
              null,
              button(
                "Sign out",
                "button button--dark-blue",
                async () => await this.signOut(),
              ),
            ),
          ),
        ),
      ),
    );
  }

  private renderDashboard(): void {
    const account = this.authentication.account;
    if (account === null) return;
    if (!account.capabilities.administerOrganization) {
      this.renderAccessDenied();
      return;
    }
    const loaded =
      this.users !== null &&
      this.registers !== null &&
      this.invitations !== null &&
      this.audit !== null;

    const main = element("main", "dashboard");
    main.id = "main-content";
    append(
      main,
      element(
        "section",
        "dashboard__intro",
        heading(1, "Organisation administration", "dashboard__title"),
      ),
    );

    if (account.role === "superadmin") {
      append(
        main,
        dashboardSelector(
          "Organisation",
          this.organizations ?? [account.organization],
          account.organization.id,
          this.busy,
          (select) => {
            select.disabled = true;
            void this.authentication
              .selectOrganization(select.value)
              .then(() => window.location.reload())
              .catch((error: unknown) => {
                this.error = messageFromError(error);
                this.renderDashboard();
              });
          },
        ),
      );
    }
    if (loaded && this.totals !== null) append(main, this.summary(this.totals));
    if (this.error !== null) append(main, this.alert(this.error, true));
    if (this.busy) {
      const progress = element("progress", "progress");
      progress.removeAttribute("value");
      progress.setAttribute("aria-label", "Loading administration data");
      append(main, progress);
    }

    if (!loaded) {
      append(
        main,
        element(
          "section",
          "loading-panel",
          element("div", "spinner", ""),
          element("p", null, "Loading administration data…"),
        ),
      );
    } else {
      append(main, this.tabs(), this.activePanel());
    }

    this.replaceRoot(this.shell(this.header(account), main));
  }

  private header(account: Account): HTMLElement {
    return dashboardHeader(
      createBrandGraphic(document, { tone: "light" }),
      account.user.displayName,
      `${account.organization.name} · ${account.role}`,
      button(
        "Sign out",
        "button button--white",
        async () => await this.signOut(),
        this.busy,
      ),
    );
  }

  private summary(totals: AdminSummary): HTMLElement {
    return element(
      "section",
      "summary-grid",
      this.metric(String(totals.userCount), "People"),
      this.metric(String(this.loadedRegisters.length), "Entitled rolls"),
      this.metric(String(totals.pendingInvitationCount), "Pending invitations"),
      this.metric(String(totals.auditEventCount), "Audit events"),
    );
  }

  private metric(value: string, label: string): HTMLElement {
    return element(
      "article",
      "metric",
      element("strong", "metric__value", value),
      element("span", "metric__label", label),
    );
  }

  private tabs(): HTMLElement {
    const tabs: readonly DashboardTab<Tab>[] = [
      { id: "users", label: "People", count: String(this.loadedUsers.length) },
      { id: "branches", label: "Branches", count: String(this.branches.length) },
      { id: "groups", label: "Groups", count: String(this.groups.length) },
      {
        id: "invitations",
        label: "Invitations",
        count: String(this.loadedInvitations.length),
      },
      { id: "audit", label: "Audit trail", count: String(this.loadedAudit.length) },
    ];
    return dashboardTabs("Administration sections", tabs, this.activeTab, (tab) => {
      this.activeTab = tab;
      this.renderDashboard();
      this.root.querySelector<HTMLButtonElement>(`#tab-${tab}`)?.focus();
    });
  }

  private activePanel(): HTMLElement {
    const panel = element("section", "data-panel");
    panel.id = `panel-${this.activeTab}`;
    panel.setAttribute("role", "tabpanel");
    panel.setAttribute("aria-labelledby", `tab-${this.activeTab}`);
    switch (this.activeTab) {
      case "users":
        append(panel, this.usersPanel());
        break;
      case "branches":
        append(panel, this.branchesPanel());
        break;
      case "groups":
        append(panel, this.groupsPanel());
        break;
      case "invitations":
        append(panel, this.invitationsPanel());
        break;
      case "audit":
        append(panel, this.auditPanel());
        break;
    }
    return panel;
  }

  private usersPanel(): HTMLElement {
    const section = element("div", null, this.panelHeading("People and access"));
    if (this.loadedUsers.length === 0) {
      append(section, this.emptyState("No users found"));
      return section;
    }
    const usersTable = table(
      [
        "Person",
        "Branch",
        "Groups",
        "Role",
        "Roll access",
        "Joined",
        { label: "", cellClassName: "data-table__action" },
      ],
      this.loadedUsers.map((user) => {
        const edit = button(
          "Edit",
          "button button--light-blue",
          async () => await this.editUser(user),
          this.busy ||
            (user.role === "superadmin" &&
              this.authentication.account?.role !== "superadmin"),
        );
        edit.prepend(buttonIcon("edit"));
        edit.setAttribute("aria-label", `Edit ${user.displayName}`);
        return {
          cells: [
            element(
              "div",
              "person",
              element("strong", null, user.displayName),
              element("span", "muted", user.email),
              element("span", "muted", user.phoneNumber),
              user.pendingEmail === null
                ? null
                : element(
                    "span",
                    "muted",
                    `Email awaiting verification: ${user.pendingEmail}`,
                  ),
            ),
            user.branch.name,
            user.groups.length === 0
              ? "None"
              : element(
                  "div",
                  "person",
                  ...user.groups.map((group) => element("span", null, group.name)),
                ),
            roleLabels[user.role],
            user.role === "superadmin"
              ? "All entitled rolls"
              : user.role === "member"
                ? "Session access"
                : `${user.electoralRegisterIds.length} roll${user.electoralRegisterIds.length === 1 ? "" : "s"}`,
            dateTimeFormatter.format(user.createdAt),
            edit,
          ],
        };
      }),
    );
    append(
      section,
      usersTable,
      this.loadMoreButton(this.userCursor, () => this.loadMoreUsers()),
    );
    return section;
  }

  private branchSelect(selectedId: string): HTMLSelectElement {
    const select = element("select", "field__control");
    select.required = true;
    select.disabled = this.busy;
    for (const branch of this.branches) {
      const option = element("option", null, branch.name);
      option.value = branch.id;
      select.append(option);
    }
    select.value = selectedId;
    return select;
  }

  private branchesPanel(): HTMLElement {
    const form = this.nameCreationForm(
      "branch",
      this.newBranchName,
      (name) => {
        this.newBranchName = name;
      },
      async (name) => {
        const branch = await this.api.createBranch(name);
        this.newBranchName = "";
        this.branches = [
          ...this.branches,
          {
            ...branch,
            memberCount: 0,
            projectCount: 0,
            leafletCount: 0,
            pendingInvitationCount: 0,
          },
        ].sort((a, b) => a.name.localeCompare(b.name));
        if (!this.invitationBranchId)
          this.invitationBranchId = this.branches[0]?.id ?? "";
      },
    );
    const branchesTable = table(
      [
        "Branch",
        "People",
        "Projects",
        "Leaflets",
        "Pending invitations",
        { label: "", cellClassName: "data-table__action" },
      ],
      this.branches.map((branch) => {
        const edit = button(
          "Edit",
          "button button--light-blue",
          () => this.editBranch(branch),
          this.busy,
        );
        edit.prepend(buttonIcon("edit"));
        edit.setAttribute("aria-label", `Edit ${branch.name}`);
        return {
          cells: [
            branch.name,
            String(branch.memberCount),
            String(branch.projectCount),
            String(branch.leafletCount),
            String(branch.pendingInvitationCount),
            edit,
          ],
        };
      }),
    );
    return element("div", null, form, this.panelHeading("Branches"), branchesTable);
  }

  private groupsPanel(): HTMLElement {
    const form = this.nameCreationForm(
      "group",
      this.newGroupName,
      (name) => {
        this.newGroupName = name;
      },
      async (name) => {
        const group = await this.api.createGroup(name);
        this.newGroupName = "";
        this.groups = [...this.groups, { ...group, memberCount: 0 }].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
      },
    );
    const groupsTable = table(
      ["Group", "People", { label: "", cellClassName: "data-table__action" }],
      this.groups.map((group) => {
        const edit = button(
          "Edit",
          "button button--light-blue",
          () => this.editGroup(group),
          this.busy,
        );
        edit.prepend(buttonIcon("edit"));
        edit.setAttribute("aria-label", `Edit ${group.name}`);
        return { cells: [group.name, String(group.memberCount), edit] };
      }),
    );
    return element(
      "div",
      null,
      form,
      this.panelHeading("Groups"),
      this.groups.length === 0 ? this.emptyState("No groups yet") : groupsTable,
    );
  }

  private nameCreationForm(
    kind: "branch" | "group",
    value: string,
    onInput: (name: string) => void,
    create: (name: string) => Promise<void>,
  ): HTMLFormElement {
    const name = profileInput("name", value, 200);
    name.id = `${kind}-name`;
    name.disabled = this.busy;
    name.addEventListener("input", () => onInput(name.value));
    const submit = element("button", "button button--dark-blue", `Create ${kind}`);
    submit.type = "submit";
    submit.disabled = this.busy;
    const form = element(
      "form",
      "invitation-form",
      heading(2, `Create a ${kind}`, "panel-heading__title"),
      element(
        "div",
        "invitation-form__controls",
        labelledField(`${humanize(kind)} name`, name),
        submit,
      ),
    );
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (this.busy || !form.reportValidity()) return;
      void this.mutate(() => create(name.value.trim()));
    });
    return form;
  }

  private invitationsPanel(): HTMLElement {
    const section = element(
      "div",
      null,
      this.invitationForm(),
      this.panelHeading("Invitation history"),
    );
    if (this.loadedInvitations.length === 0) {
      append(section, this.emptyState("No invitations found"));
      return section;
    }
    const invitationsTable = table(
      [
        "Recipient",
        "Branch",
        "Kind",
        "Status",
        "Issued",
        { label: "", cellClassName: "data-table__action" },
      ],
      this.loadedInvitations.map((invitation) => {
        const status = invitationStatus(invitation);
        const revoke =
          status === "pending"
            ? button(
                "Revoke",
                "button button--dark-blue",
                async () => await this.revokeInvitation(invitation),
                this.busy,
              )
            : "";
        return {
          cells: [
            invitation.intendedEmail ?? "Organisation QR invitation",
            invitation.branch.name,
            humanize(invitation.kind),
            this.badge(humanize(status), `status status--${status}`),
            dateTimeFormatter.format(invitation.issuedAt),
            revoke,
          ],
        };
      }),
    );
    append(
      section,
      invitationsTable,
      this.loadMoreButton(this.invitationCursor, () => this.loadMoreInvitations()),
    );
    return section;
  }

  private invitationForm(): HTMLFormElement {
    const title = heading(2, "Send an invitation", "panel-heading__title");
    title.id = "invitation-form-title";
    const email = element("input", "field__control");
    email.type = "email";
    email.name = "email";
    email.id = "invitation-email";
    email.required = true;
    email.maxLength = 254;
    email.autocomplete = "off";
    email.value = this.invitationEmail;
    email.disabled = this.busy;
    email.addEventListener("input", () => {
      this.invitationEmail = email.value;
    });
    const label = element("label", "field__label", "Email address");
    label.htmlFor = email.id;
    const branch = this.branchSelect(this.invitationBranchId);
    branch.id = "invitation-branch";
    branch.addEventListener("change", () => {
      this.invitationBranchId = branch.value;
    });
    const submit = element("button", "button button--dark-blue", "Send invitation");
    submit.type = "submit";
    submit.disabled = this.busy || this.branches.length === 0;
    const notice = element("p", "invitation-form__notice", this.invitationNotice ?? "");
    notice.setAttribute("role", "status");
    const form = element(
      "form",
      "invitation-form",
      title,
      element(
        "div",
        "invitation-form__controls",
        element("div", "field", label, email),
        element("label", "field", element("span", "field__label", "Branch"), branch),
        submit,
      ),
      notice,
    );
    form.setAttribute("aria-labelledby", title.id);
    form.setAttribute("aria-busy", String(this.busy));
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (this.busy || !form.reportValidity()) return;
      void this.createInvitation(email.value.trim(), branch.value);
    });
    return form;
  }

  private auditPanel(): HTMLElement {
    const section = element("div", null, this.panelHeading("Audit trail"));
    if (this.loadedAudit.length === 0) {
      append(section, this.emptyState("No audit events found"));
      return section;
    }
    const list = element("div", "audit-list");
    for (const event of this.loadedAudit) {
      const details = element("details", "audit-event");
      const summary = element(
        "summary",
        "audit-event__summary",
        element("span", "audit-event__action", humanize(event.action)),
        element(
          "span",
          "audit-event__meta",
          `${event.actorKind}: ${event.actorIdentifier} · ${dateTimeFormatter.format(event.occurredAt)}`,
        ),
      );
      const body = element("div", "audit-event__body");
      if (event.targetUserId !== null) {
        append(body, this.auditFact("User", this.userLabel(event.targetUserId)));
      }
      if (event.invitationId !== null) {
        append(body, this.auditFact("Invitation", event.invitationId));
      }
      if (event.electoralRegisterId !== null) {
        append(
          body,
          this.auditFact(
            "Electoral roll",
            this.registerLabel(event.electoralRegisterId) ?? event.electoralRegisterId,
          ),
        );
      }
      if (Object.keys(event.details).length > 0) {
        const code = element(
          "pre",
          "audit-event__json",
          JSON.stringify(event.details, null, 2),
        );
        append(body, code);
      }
      append(details, summary, body);
      list.append(details);
    }
    append(
      section,
      list,
      this.loadMoreButton(this.auditCursor, () => this.loadMoreAudit()),
    );
    return section;
  }

  private async editUser(user: AdminUser): Promise<void> {
    const changes = await this.userDialog(user);
    if (changes === null) return;
    const branchChanged = changes.branchId !== user.branch.id;
    const groupsChanged =
      changes.groupIds.size !== user.groups.length ||
      user.groups.some((group) => !changes.groupIds.has(group.id));
    const profileChanged =
      changes.firstName !== user.firstName ||
      changes.lastName !== user.lastName ||
      changes.phoneNumber !== user.phoneNumber ||
      changes.email.toLowerCase() !== (user.pendingEmail ?? user.email).toLowerCase();
    const accessChanged =
      changes.role !== user.role ||
      changes.registerIds.size !== user.electoralRegisterIds.length ||
      user.electoralRegisterIds.some((id) => !changes.registerIds.has(id));
    if (!profileChanged && !branchChanged && !groupsChanged && !accessChanged) return;
    await this.mutate(async () => {
      const updated = await this.api.updateUser(user, changes);
      this.users = this.loadedUsers.map((candidate) =>
        candidate.id === updated.id ? updated : candidate,
      );
      this.groups = this.groups.map((group) => ({
        ...group,
        memberCount:
          group.memberCount +
          Number(updated.groups.some((candidate) => candidate.id === group.id)) -
          Number(user.groups.some((candidate) => candidate.id === group.id)),
      }));
      if (updated.branch.id !== user.branch.id) {
        this.branches = this.branches.map((branch) => ({
          ...branch,
          memberCount:
            branch.memberCount +
            Number(branch.id === updated.branch.id) -
            Number(branch.id === user.branch.id),
        }));
      }
      if (updated.id === this.authentication.account?.user.id) {
        await this.authentication.refreshAccount();
      }
    });
  }

  private userDialog(user: AdminUser): Promise<UserChanges | null> {
    const entitledIds = new Set(this.loadedRegisters.map((register) => register.id));
    const unavailableIds = user.electoralRegisterIds.filter(
      (id) => !entitledIds.has(id),
    );
    const selectedIds = new Set(
      user.electoralRegisterIds.filter((id) => entitledIds.has(id)),
    );
    const dialog = element("dialog", "dialog");
    const titleId = `edit-user-${cssSafeId(user.id)}`;
    dialog.setAttribute("aria-labelledby", titleId);
    const form = element("form");
    const title = heading(2, user.displayName, "dialog__title");
    title.id = titleId;
    const firstName = profileInput(
      "first_name",
      user.firstName,
      200,
      "text",
      "given-name",
    );
    const lastName = profileInput(
      "last_name",
      user.lastName,
      200,
      "text",
      "family-name",
    );
    const phoneNumber = profileInput(
      "phone_number",
      user.phoneNumber,
      64,
      "tel",
      "tel",
    );
    const email = profileInput(
      "email",
      user.pendingEmail ?? user.email,
      254,
      "email",
      "email",
    );
    const emailHelp = element(
      "p",
      "muted",
      user.pendingEmail === null
        ? "Changing the login email sends a verification link to the new address. The current email stays active until the member verifies the change."
        : `Awaiting verification at ${user.pendingEmail}. The current login email is ${user.email}. Enter the current email to cancel the pending change.`,
    );
    emailHelp.id = `${titleId}-email-help`;
    email.setAttribute("aria-describedby", emailHelp.id);
    const branchSelect = this.branchSelect(user.branch.id);
    branchSelect.id = `${titleId}-branch`;
    branchSelect.name = "branch";
    const memberships = element("fieldset", "check-group");
    memberships.name = "groups";
    memberships.append(element("legend", "field__label", "Groups"));
    const knownGroupIds = new Set(this.groups.map((group) => group.id));
    const groups = [
      ...this.groups,
      ...user.groups.filter((group) => !knownGroupIds.has(group.id)),
    ].sort((a, b) => a.name.localeCompare(b.name));
    for (const group of groups) {
      const checkbox = element("input", "check-row__input");
      checkbox.type = "checkbox";
      checkbox.name = "group_ids";
      checkbox.value = group.id;
      checkbox.checked = user.groups.some((candidate) => candidate.id === group.id);
      memberships.append(
        element("label", "check-row", checkbox, element("span", null, group.name)),
      );
    }
    if (groups.length === 0)
      memberships.append(
        element("p", "muted", "Create groups in the Groups tab to assign them here."),
      );
    const isSuperadmin = user.role === "superadmin";
    const roleSelect = element("select", "field__control");
    roleSelect.id = `${titleId}-role`;
    roleSelect.name = "role";
    roleSelect.disabled = isSuperadmin;
    for (const role of roles.filter(
      (value) => value !== "superadmin" || isSuperadmin,
    )) {
      const option = element("option", null, roleLabels[role]);
      option.value = role;
      roleSelect.append(option);
    }
    roleSelect.value = user.role;
    const assignments = element("fieldset", "check-group");
    assignments.name = "electoral_register_ids";
    assignments.append(element("legend", "field__label", "Electoral-roll assignments"));
    for (const register of this.loadedRegisters) {
      const checkbox = element("input", "check-row__input");
      checkbox.type = "checkbox";
      checkbox.value = register.id;
      checkbox.checked = selectedIds.has(register.id);
      const label = element(
        "label",
        "check-row",
        checkbox,
        element(
          "span",
          null,
          element("strong", null, register.label),
          element("small", "muted", register.id),
        ),
      );
      assignments.append(label);
    }
    const syncAssignments = (): void => {
      assignments.disabled = roleSelect.value === "member";
    };
    roleSelect.addEventListener("change", syncAssignments);
    syncAssignments();
    const actions = element(
      "div",
      "dialog__actions",
      button("Cancel", "button button--white", () => dialog.close("cancel")),
    );
    const save = element(
      "button",
      "button button--dark-blue",
      buttonIcon("save"),
      "Save changes",
    );
    save.type = "submit";
    actions.append(save);
    append(
      form,
      element(
        "header",
        "dialog__header",
        element("p", "eyebrow", "Edit person"),
        title,
        element("p", "muted", user.email),
      ),
      labelledField("First name", firstName),
      labelledField("Last name", lastName),
      labelledField("Phone number", phoneNumber),
      labelledField("Login email", email),
      emailHelp,
      element(
        "label",
        "field",
        element("span", "field__label", "Branch"),
        branchSelect,
      ),
      memberships,
      unavailableIds.length === 0 || isSuperadmin
        ? null
        : this.alert(
            `Assignments outside the organisation’s current entitlement will be removed when saved: ${unavailableIds.join(", ")}.`,
          ),
      element("label", "field", element("span", "field__label", "Role"), roleSelect),
      isSuperadmin ? null : assignments,
      element(
        "p",
        "muted",
        isSuperadmin
          ? "Superadmins have access to all entitled rolls. Only a server operator can change superadmin access."
          : "Members receive temporary roll access only through active canvassing sessions.",
      ),
      actions,
    );
    dialog.append(form);
    document.body.append(dialog);

    return new Promise((resolve) => {
      let result: UserChanges | null = null;
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        const role = roleSelect.value;
        if (!roles.some((candidate) => candidate === role)) return;
        const registerIds = new Set(
          isSuperadmin
            ? user.electoralRegisterIds
            : role === "member"
              ? []
              : [
                  ...assignments.querySelectorAll<HTMLInputElement>("input:checked"),
                ].map((input) => input.value),
        );
        result = {
          firstName: firstName.value.trim(),
          lastName: lastName.value.trim(),
          phoneNumber: phoneNumber.value.trim(),
          email: email.value.trim(),
          branchId: branchSelect.value,
          groupIds: new Set(
            [...memberships.querySelectorAll<HTMLInputElement>("input:checked")].map(
              (input) => input.value,
            ),
          ),
          role: role as Role,
          registerIds,
        };
        dialog.close("save");
      });
      dialog.addEventListener(
        "close",
        () => {
          dialog.remove();
          resolve(dialog.returnValue === "save" ? result : null);
        },
        { once: true },
      );
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close("cancel");
      });
      dialog.showModal();
    });
  }

  private async editBranch(branch: BranchSummary): Promise<void> {
    const canDelete =
      branch.memberCount === 0 &&
      branch.projectCount === 0 &&
      branch.leafletCount === 0 &&
      branch.pendingInvitationCount === 0;
    const changes = await this.nameDialog(
      "branch",
      branch,
      canDelete,
      canDelete
        ? "This branch is empty and can be deleted. Invitation and audit history will be retained."
        : "To delete this branch, move its members, delete its projects and leaflets, and resolve its pending invitations.",
      `Delete “${branch.name}”? Invitation and audit history will be retained.`,
    );
    if (
      changes === null ||
      (changes.action === "rename" && changes.name === branch.name)
    )
      return;
    await this.mutate(async () => {
      if (changes.action === "delete") {
        await this.api.deleteBranch(branch.id);
        this.branches = this.branches.filter((candidate) => candidate.id !== branch.id);
        if (this.invitationBranchId === branch.id)
          this.invitationBranchId = this.branches[0]?.id ?? "";
      } else {
        const renamed = await this.api.renameBranch(branch, changes.name);
        this.branches = this.branches
          .map((candidate) =>
            candidate.id === branch.id ? { ...candidate, ...renamed } : candidate,
          )
          .sort((a, b) => a.name.localeCompare(b.name));
        this.users = this.loadedUsers.map((user) =>
          user.branch.id === branch.id ? { ...user, branch: renamed } : user,
        );
        this.invitations = this.loadedInvitations.map((invitation) =>
          invitation.branch.id === branch.id
            ? { ...invitation, branch: renamed }
            : invitation,
        );
      }
    });
  }

  private async editGroup(group: GroupSummary): Promise<void> {
    const changes = await this.nameDialog(
      "group",
      group,
      true,
      "Deleting this group removes its memberships. Users and audit history are retained.",
      `Delete “${group.name}” and all its memberships? Users and audit history will be retained.`,
    );
    if (
      changes === null ||
      (changes.action === "rename" && changes.name === group.name)
    )
      return;
    await this.mutate(async () => {
      if (changes.action === "delete") {
        await this.api.deleteGroup(group.id);
        this.groups = this.groups.filter((candidate) => candidate.id !== group.id);
        this.users = this.loadedUsers.map((user) =>
          user.groups.some((candidate) => candidate.id === group.id)
            ? {
                ...user,
                groups: user.groups.filter((candidate) => candidate.id !== group.id),
              }
            : user,
        );
        try {
          const [users, groups] = await Promise.all([
            this.api.listUsers(),
            this.api.listGroups(),
          ]);
          this.users = users.items;
          this.userCursor = users.nextCursor;
          this.groups = groups;
        } catch (error) {
          if (error instanceof AuthenticationRequiredError) throw error;
          throw new Error(
            `The group was deleted, but people and groups could not be refreshed: ${messageFromError(error)}`,
          );
        }
      } else {
        const renamed = await this.api.renameGroup(group, changes.name);
        this.groups = this.groups
          .map((candidate) =>
            candidate.id === group.id ? { ...candidate, ...renamed } : candidate,
          )
          .sort((a, b) => a.name.localeCompare(b.name));
        this.users = this.loadedUsers.map((user) => ({
          ...user,
          groups: user.groups
            .map((candidate) => (candidate.id === group.id ? renamed : candidate))
            .sort((a, b) => a.name.localeCompare(b.name)),
        }));
      }
    });
  }

  private nameDialog(
    kind: "branch" | "group",
    entity: { readonly id: string; readonly name: string },
    canDelete: boolean,
    deleteHelp: string,
    deleteConfirmation: string,
  ): Promise<NameChanges | null> {
    const dialog = element("dialog", "dialog");
    const title = heading(2, entity.name, "dialog__title");
    title.id = `edit-${kind}-${cssSafeId(entity.id)}`;
    dialog.setAttribute("aria-labelledby", title.id);
    const name = profileInput("name", entity.name, 200);
    const save = element(
      "button",
      "button button--dark-blue",
      buttonIcon("save"),
      "Save changes",
    );
    save.type = "submit";
    const explanation = element("p", "muted", deleteHelp);
    explanation.id = `${title.id}-delete-help`;
    const remove = button(
      `Delete ${kind}`,
      "button button--danger",
      () => {
        explanation.textContent = deleteConfirmation;
        remove.hidden = true;
        confirmDelete.hidden = false;
        save.disabled = true;
        name.disabled = true;
        confirmDelete.focus();
      },
      !canDelete,
    );
    remove.prepend(buttonIcon("delete"));
    remove.setAttribute("aria-describedby", explanation.id);
    const confirmDelete = button("Confirm delete", "button button--danger", () =>
      dialog.close("delete"),
    );
    confirmDelete.prepend(buttonIcon("delete"));
    confirmDelete.hidden = true;
    const form = element(
      "form",
      null,
      element(
        "header",
        "dialog__header",
        element("p", "eyebrow", `Edit ${kind}`),
        title,
      ),
      labelledField(`${humanize(kind)} name`, name),
      explanation,
      element(
        "div",
        "dialog__actions",
        remove,
        confirmDelete,
        button("Cancel", "button button--white", () => dialog.close("cancel")),
        save,
      ),
    );
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (save.disabled || !form.reportValidity()) return;
      dialog.close("rename");
    });
    dialog.append(form);
    document.body.append(dialog);
    return new Promise((resolve) => {
      dialog.addEventListener(
        "close",
        () => {
          dialog.remove();
          resolve(
            dialog.returnValue === "delete" && canDelete
              ? { action: "delete" }
              : dialog.returnValue === "rename"
                ? { action: "rename", name: name.value.trim() }
                : null,
          );
        },
        { once: true },
      );
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close("cancel");
      });
      dialog.showModal();
    });
  }

  private async createInvitation(email: string, branchId: string): Promise<void> {
    this.invitationNotice = null;
    await this.mutate(async () => {
      const invitation = await this.api.createInvitation(email, branchId);
      this.invitations = [invitation, ...this.loadedInvitations];
      this.invitationEmail = "";
      this.branches = this.branches.map((branch) =>
        branch.id === branchId
          ? { ...branch, pendingInvitationCount: branch.pendingInvitationCount + 1 }
          : branch,
      );
      this.invitationNotice = `Invitation email queued for ${invitation.intendedEmail}.`;
    });
  }

  private async revokeInvitation(invitation: AdminInvitation): Promise<void> {
    const target = invitation.intendedEmail ?? "organisation QR invitation";
    if (!window.confirm(`Revoke the pending invitation for ${target}?`)) return;
    await this.mutate(async () => {
      await this.api.revokeInvitation(invitation.id);
      const page = await this.api.listInvitations();
      this.invitations = page.items;
      this.invitationCursor = page.nextCursor;
      this.branches = await this.api.listBranches();
    });
  }

  private async loadMoreUsers(): Promise<void> {
    await this.loadMore(
      this.userCursor,
      (page: Page<AdminUser>) => {
        this.users = [...this.loadedUsers, ...page.items];
        this.userCursor = page.nextCursor;
      },
      (cursor) => this.api.listUsers(cursor),
    );
  }

  private async loadMoreInvitations(): Promise<void> {
    await this.loadMore(
      this.invitationCursor,
      (page: Page<AdminInvitation>) => {
        this.invitations = [...this.loadedInvitations, ...page.items];
        this.invitationCursor = page.nextCursor;
      },
      (cursor) => this.api.listInvitations(cursor),
    );
  }

  private async loadMoreAudit(): Promise<void> {
    await this.loadMore(
      this.auditCursor,
      (page: Page<AdminAuditEvent>) => {
        this.audit = [...this.loadedAudit, ...page.items];
        this.auditCursor = page.nextCursor;
      },
      (cursor) => this.api.listAudit(cursor),
    );
  }

  private async loadMore<T>(
    cursor: string | null,
    apply: (page: Page<T>) => void,
    request: (cursor: string) => Promise<Page<T>>,
  ): Promise<void> {
    if (cursor === null) return;
    await this.perform(async () => apply(await request(cursor)));
  }

  private async mutate(operation: () => Promise<void>): Promise<void> {
    await this.perform(async () => {
      this.totals = null;
      try {
        await operation();
      } catch (error) {
        if (error instanceof AuthenticationRequiredError) throw error;
        this.error = messageFromError(error);
      }
      if (this.authentication.account?.capabilities.administerOrganization) {
        const [summary, audit] = await Promise.allSettled([
          this.api.getSummary(),
          this.api.listAudit(),
        ]);
        for (const [result, label] of [
          [summary, "Organisation totals"],
          [audit, "Audit history"],
        ] as const) {
          if (result.status === "rejected") {
            if (result.reason instanceof AuthenticationRequiredError)
              throw result.reason;
            const message = `${label} could not be refreshed: ${messageFromError(result.reason)}`;
            this.error = this.error === null ? message : `${this.error} ${message}`;
          }
        }
        if (summary.status === "fulfilled") this.totals = summary.value;
        if (audit.status === "fulfilled") {
          this.audit = audit.value.items;
          this.auditCursor = audit.value.nextCursor;
        }
      }
    });
  }

  private async perform(operation: () => Promise<void>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.error = null;
    this.renderDashboard();
    try {
      await operation();
    } catch (error) {
      if (this.handleAuthenticationFailure(error)) return;
      this.error = messageFromError(error);
    } finally {
      this.busy = false;
      this.renderCurrentAuthenticatedState();
    }
  }

  private renderCurrentAuthenticatedState(): void {
    const account = this.authentication.account;
    if (account === null) return;
    if (account.capabilities.administerOrganization) {
      this.renderDashboard();
    } else {
      this.renderAccessDenied();
    }
  }

  private handleAuthenticationFailure(error: unknown): boolean {
    if (!(error instanceof AuthenticationRequiredError)) return false;
    this.organizations = null;
    this.renderSignIn(error.message);
    return true;
  }

  private async signOut(): Promise<void> {
    this.organizations = null;
    this.renderLoading("Signing out");
    try {
      await this.authentication.signOut();
      this.renderSignIn();
    } catch (error) {
      this.renderSignIn(messageFromError(error));
    }
  }

  private panelHeading(title: string): HTMLElement {
    return element(
      "header",
      "panel-heading",
      heading(2, title, "panel-heading__title"),
    );
  }

  private emptyState(message: string): HTMLElement {
    return element(
      "div",
      "empty-state",
      heading(3, message),
      element("p", "muted", "Reload to check again."),
    );
  }

  private alert(message: string, dismissible = false): HTMLElement {
    const alert = element("div", "alert", element("p", null, message));
    alert.setAttribute("role", "alert");
    if (dismissible) {
      append(
        alert,
        button("Dismiss", "button button--white", () => {
          this.error = null;
          this.renderDashboard();
        }),
      );
    }
    return alert;
  }

  private badge(label: string, className: string): HTMLElement {
    return element("span", `badge ${className}`, label);
  }

  private loadMoreButton(
    cursor: string | null,
    action: () => Promise<void>,
  ): HTMLElement | null {
    return cursor === null
      ? null
      : element(
          "div",
          "load-more",
          button("Load more", "button button--light-blue", action, this.busy),
        );
  }

  private auditFact(label: string, value: string): HTMLElement {
    return element(
      "p",
      "audit-event__fact",
      element("strong", null, `${label}: `),
      value,
    );
  }

  private registerLabel(id: string | null): string | null {
    if (id === null) return null;
    const register = this.registers?.find((candidate) => candidate.id === id);
    return register === undefined ? id : `${register.label} (${id})`;
  }

  private userLabel(id: string): string {
    const user = this.users?.find((candidate) => candidate.id === id);
    return user === undefined ? id : `${user.displayName} (${id})`;
  }

  private get loadedUsers(): readonly AdminUser[] {
    return requireLoaded(this.users, "users");
  }

  private get loadedRegisters(): readonly ElectoralRegister[] {
    return requireLoaded(this.registers, "electoral registers");
  }

  private get loadedInvitations(): readonly AdminInvitation[] {
    return requireLoaded(this.invitations, "invitations");
  }

  private get loadedAudit(): readonly AdminAuditEvent[] {
    return requireLoaded(this.audit, "audit events");
  }

  private shell(...content: readonly Node[]): DocumentFragment {
    const fragment = document.createDocumentFragment();
    fragment.append(...content);
    return fragment;
  }

  private replaceRoot(content: DocumentFragment): void {
    const main = content.querySelector("main");
    if (main !== null && main.id === "") main.id = "main-content";
    this.root.replaceChildren(content);
  }
}

function humanize(value: string): string {
  return value
    .split("_")
    .filter((part) => part.length > 0)
    .map((part) => `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}`)
    .join(" ");
}

function profileInput(
  name: string,
  value: string,
  maxLength: number,
  type = "text",
  autocomplete: AutoFill = "off",
): HTMLInputElement {
  const input = element("input", "field__control");
  input.name = name;
  input.value = value;
  input.type = type;
  input.autocomplete = autocomplete;
  input.required = true;
  input.maxLength = maxLength;
  return input;
}

function labelledField(label: string, control: HTMLElement): HTMLLabelElement {
  return element("label", "field", element("span", "field__label", label), control);
}

function messageFromError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function cssSafeId(value: string): string {
  return value.replaceAll(/[^A-Za-z0-9_-]/g, "-");
}

function requireLoaded<T>(value: T | null, description: string): T {
  if (value === null) {
    throw new Error(`${description} were rendered before loading completed`);
  }
  return value;
}
